// 节假日数据 - 使用公共API获取（无需API key）
// 优先级：Supabase缓存 > JiejiariAPI > Nager.Date（备用）
// Web 应用版本

import { supabase, isSupabaseAvailable } from '@ext-bookmark/shared/api';

export interface Holiday {
  date: string; // YYYY-MM-DD 格式
  name: string; // 节假日名称
  localName?: string; // 本地名称
  type: 'national' | 'statutory' | 'traditional' | 'public'; // 节假日类型
  isWorkday?: boolean; // 是否为工作日（调休时可能为true）
  isOffDay?: boolean; // 是否为休息日（节假日或调休后的休息日）
}

// 节假日数据缓存（按年份存储，内存缓存）
const holidayCache: Map<number, Holiday[]> = new Map();

// 正在进行的请求（防止重复请求）
const pendingRequests: Map<number, Promise<Holiday[]>> = new Map();

/** 本地时区 YYYY-MM-DD，避免 toISOString 的 UTC 偏差 */
function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// API配置（全部免费，无需API key）
const API_CONFIG = {
  // JiejiariAPI（免费，支持调休信息，优先使用）
  jiejiari: {
    baseUrl: 'https://api.jiejiariapi.com/v1',
  },
  // Nager.Date API（免费，无需API key，作为备用）
  nagerDate: {
    baseUrl: 'https://date.nager.at/api/v3',
    countryCode: 'CN', // 中国
  },
};

/**
 * 从Nager.Date API获取节假日数据
 */
async function fetchHolidaysFromNagerDate(year: number): Promise<Holiday[]> {
  try {
    const url = `${API_CONFIG.nagerDate.baseUrl}/PublicHolidays/${year}/${API_CONFIG.nagerDate.countryCode}`;
    
    // 创建 AbortController 用于超时控制
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 30000); // 30秒超时
    
    const response = await fetch(url, {
      signal: controller.signal,
    });
    
    // 清除超时定时器
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // 转换API数据格式
    return data.map((item: any) => ({
      date: item.date,
      name: item.localName || item.name,
      localName: item.localName,
      type: 'public' as const,
      isWorkday: false,
      isOffDay: true,
    }));
  } catch (error) {
    console.error('Failed to fetch holidays from Nager.Date:', error);
    return [];
  }
}

/**
 * 从Supabase获取缓存的节假日数据
 */
async function fetchHolidaysFromSupabase(year: number): Promise<Holiday[]> {
  if (!isSupabaseAvailable() || !supabase) {
    return [];
  }

  try {
    const { data, error } = await supabase
      .from('holidays_cache')
      .select('holidays_data')
      .eq('year', year)
      .single();

    if (error) {
      // 如果没有找到数据，返回空数组（不是错误）
      if (error.code === 'PGRST116') {
        return [];
      }
      throw error;
    }

    if (data && data.holidays_data) {
      return data.holidays_data as Holiday[];
    }

    return [];
  } catch (error) {
    console.error('Failed to fetch holidays from Supabase:', error);
    return [];
  }
}

/**
 * 保存节假日数据到Supabase
 */
async function saveHolidaysToSupabase(year: number, holidays: Holiday[]): Promise<void> {
  if (!isSupabaseAvailable() || !supabase || holidays.length === 0) {
    return;
  }

  try {
    const { error } = await supabase
      .from('holidays_cache')
      .upsert(
        {
          year,
          holidays_data: holidays,
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: 'year',
        }
      );

    if (error) {
      console.error('Failed to save holidays to Supabase:', error);
    }
  } catch (error) {
    console.error('Failed to save holidays to Supabase:', error);
  }
}

/**
 * 从JiejiariAPI获取节假日数据（免费，支持调休）
 * Web 应用版本：直接使用 fetch
 */
async function fetchHolidaysFromJiejiari(year: number): Promise<Holiday[]> {
  try {
    // JiejiariAPI 免费接口，无需API key
    const url = `${API_CONFIG.jiejiari.baseUrl}/holidays/${year}`;
    
    try {
      // Web 应用环境：直接使用 fetch
      const fetchResponse = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!fetchResponse.ok) {
        throw new Error(`HTTP error! status: ${fetchResponse.status}`);
      }
      
      const data = await fetchResponse.json();
      
      // 转换API数据格式 - 支持多种响应格式
      let holidays: Holiday[] = [];
      
      // 格式1: { data: [...] }
      if (data.data && Array.isArray(data.data)) {
        holidays = data.data.map((item: any) => {
          const isWorkday = item.isWorkday === true || item.work === true || item.rest === false;
          const isOffDay = item.isOffDay === true || item.rest === true || item.off === true || !isWorkday;
          return {
            date: item.date || item.holiday,
            name: item.name || item.holidayName,
            localName: item.name,
            type: item.type || 'public',
            isWorkday,
            isOffDay: isOffDay || (!isWorkday && item.name),
          };
        });
      }
      // 格式2: 直接是数组
      else if (Array.isArray(data)) {
        holidays = data.map((item: any) => {
          const isWorkday = item.isWorkday === true || item.work === true || item.rest === false;
          const isOffDay = item.isOffDay === true || item.rest === true || item.off === true || !isWorkday;
          return {
            date: item.date || item.holiday,
            name: item.name || item.holidayName,
            localName: item.name,
            type: item.type || 'public',
            isWorkday,
            isOffDay: isOffDay || (!isWorkday && item.name),
          };
        });
      }
      // 格式3: { [date]: { date, name, isOffDay } } 对象格式（JiejiariAPI 标准格式）
      else if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
        holidays = Object.entries(data).map(([dateKey, item]: [string, any]) => {
          // JiejiariAPI 格式：{ "2026-01-01": { "date": "2026-01-01", "name": "元旦", "isOffDay": true } }
          const date = item.date || dateKey;
          const isOffDay = item.isOffDay === true;
          const isWorkday = item.isOffDay === false; // isOffDay: false 表示调休工作日（需要补班）
          
          return {
            date: date,
            name: item.name,
            localName: item.name,
            type: 'public' as const,
            isWorkday,
            isOffDay,
          };
        });
      }
      
      if (holidays.length > 0) {
        return holidays;
      }
    } catch (e: any) {
      console.error(`[Holidays] 请求失败:`, e.message);
      return [];
    }
    
    return [];
  } catch (error: any) {
    console.error('[Holidays] Failed to fetch holidays from JiejiariAPI:', error);
    return [];
  }
}

/**
 * 获取指定年份的节假日数据（带缓存）
 * 优先级：内存缓存 > Supabase缓存 > JiejiariAPI > Nager.Date（备用）
 */
export async function getHolidaysForYear(year: number): Promise<Holiday[]> {
  // 1. 检查内存缓存
  if (holidayCache.has(year)) {
    return holidayCache.get(year)!;
  }

  // 创建请求 Promise（防止重复请求）
  const existingRequest = pendingRequests.get(year);
  if (existingRequest) {
    return existingRequest;
  }

  const requestPromise = (async () => {
    let holidays: Holiday[] = [];
    
    // 2. 优先从Supabase获取缓存数据
    holidays = await fetchHolidaysFromSupabase(year);
    
    // 3. 如果Supabase没有数据，从JiejiariAPI获取
    if (holidays.length === 0) {
      holidays = await fetchHolidaysFromJiejiari(year);
      
      // 4. 如果JiejiariAPI失败，回退到Nager.Date
      if (holidays.length === 0) {
        holidays = await fetchHolidaysFromNagerDate(year);
      }
      
      // 5. 如果从API获取到数据，保存到Supabase
      if (holidays.length > 0) {
        await saveHolidaysToSupabase(year, holidays);
      }
    }

    // 6. 缓存到内存
    if (holidays.length > 0) {
      holidayCache.set(year, holidays);
    }

    return holidays;
  })();

  // 保存请求 Promise
  pendingRequests.set(year, requestPromise);

  try {
    const result = await requestPromise;
    return result;
  } finally {
    // 请求完成后移除
    pendingRequests.delete(year);
  }
}

/**
 * 获取指定日期的节假日信息
 */
export async function getHoliday(date: Date): Promise<Holiday | null> {
  const year = date.getFullYear();
  const holidays = await getHolidaysForYear(year);
  const dateStr = toLocalDateStr(date);
  return holidays.find(h => h.date === dateStr) || null;
}

/**
 * 获取指定日期范围内的所有节假日
 */
export async function getHolidaysInRange(startDate: Date, endDate: Date): Promise<Holiday[]> {
  const _holidays: Holiday[] = [];
  void _holidays;
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  
  // 获取所有相关年份的节假日
  const yearPromises: Promise<Holiday[]>[] = [];
  for (let year = startYear; year <= endYear; year++) {
    yearPromises.push(getHolidaysForYear(year));
  }
  
  const allHolidays = await Promise.all(yearPromises);
  const flatHolidays = allHolidays.flat();
  
  // 过滤日期范围（使用本地时区，避免 UTC 偏差）
  const startStr = toLocalDateStr(startDate);
  const endStr = toLocalDateStr(endDate);
  return flatHolidays.filter(h => h.date >= startStr && h.date <= endStr);
}

/**
 * 判断指定日期是否为节假日
 */
export async function isHoliday(date: Date): Promise<boolean> {
  const holiday = await getHoliday(date);
  return holiday !== null && !holiday.isWorkday;
}

/**
 * 预加载当前年份和下一年的节假日数据
 */
export async function preloadHolidays(): Promise<void> {
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  
  await Promise.all([
    getHolidaysForYear(currentYear),
    getHolidaysForYear(nextYear),
  ]);
}
